import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, describeAuthFormError, formErrorClassName, secondaryTextClassName, useAuth, type AuthFormError, isInvitationContinuation } from '@monecolier/ui';
import { acceptGuardianInvitation } from '@monecolier/api-client';
import { invitationContinuation } from '../lib/invitationContinuation';

/**
 * PAR-04 — page cible du lien envoyé par email au second parent/tuteur
 * (`POST /declared-children/:id/guardians`, voir
 * `lib/declaredChildGuardianEmail.ts` côté apps/api). `token` porté par la
 * query string (`?token=...`) — même contrat qu'`AcceptInvitation.tsx`
 * (AUT-08, web-admin). AUT-33 : jeton propagé à `/register`/`/login` (voir
 * `AcceptParentInvitation.tsx`), acceptation automatique au retour, puis
 * accueil direct — plus de retour manuel sur le lien email.
 *
 * ⚠️ Contrairement à AUT-08, accepter exige en plus un PROFIL PARENT déjà
 * complété (`400 parent_profile_required` sinon, voir routes/
 * declaredChildGuardians.ts) — un compte fraîchement inscrit sans téléphone
 * (PAR-03) n'en a pas forcément un ; ce cas est affiché comme une erreur
 * standard (le bandeau de complétion existe déjà sur `Home.tsx`, voir
 * "Vérification post-hoc PAR-03" dans docs/history/PAR.md), pas un flux dédié ici.
 */
export function AcceptGuardianInvitation() {
  const { accessToken, apiClient, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  // AUT-33 : retour depuis /register ou /login (jeton propagé, voir
  // lib/invitationContinuation.ts) — sur succès, accueil direct.
  const continueAfterAuth = isInvitationContinuation(useLocation().state);
  const authContinuation = token ? { kind: 'guardian' as const, token } : null;

  const [status, setStatus] = useState<'idle' | 'accepting' | 'accepted' | 'error'>('idle');
  const [error, setError] = useState<AuthFormError | null>(null);

  // AUT-33 : un seul appel d'acceptation par jeton — en dev, StrictMode rejoue
  // cet effet avant que `status` ne passe à 'accepting' (même garde qu'AUT-32,
  // web-admin/AcceptInvitation.tsx : un 409 "déjà acceptée" revenu en dernier
  // afficherait une erreur malgré un rattachement réussi).
  const acceptStartedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !accessToken || !token || status !== 'idle') return;
    if (acceptStartedFor.current === token) return;
    acceptStartedFor.current = token;
    setStatus('accepting');
    acceptGuardianInvitation(apiClient, token, accessToken)
      .then(() => setStatus('accepted'))
      .catch((err) => {
        setError(describeAuthFormError(err));
        setStatus('error');
      });
  }, [isAuthenticated, accessToken, apiClient, token, status]);

  if (!token) {
    return (
      <Card title={t('acceptGuardianInvitation.title')}>
        <p role="alert" data-testid="accept-guardian-invitation-missing-token" className={formErrorClassName('generic')}>
          {t('acceptGuardianInvitation.missingToken')}
        </p>
      </Card>
    );
  }

  if (!isAuthenticated) {
    return (
      <Card title={t('acceptGuardianInvitation.anonymousTitle')}>
        <p className={secondaryTextClassName}>{t('acceptGuardianInvitation.anonymousBody')}</p>
        <div className="flex gap-2">
          <Link to={invitationContinuation.authPath('/login', authContinuation)}>
            <Button type="button">{t('acceptGuardianInvitation.login')}</Button>
          </Link>
          <Link to={invitationContinuation.authPath('/register', authContinuation)}>
            <Button type="button" variant="secondary">
              {t('acceptGuardianInvitation.createAccount')}
            </Button>
          </Link>
        </div>
      </Card>
    );
  }

  if (status === 'accepted') {
    if (continueAfterAuth) return <Navigate to="/" replace />;
    return (
      <Card title={t('acceptGuardianInvitation.acceptedTitle')}>
        <p data-testid="accept-guardian-invitation-success">{t('acceptGuardianInvitation.acceptedBody')}</p>
        <Link to="/">
          <Button type="button">{t('acceptGuardianInvitation.goToHome')}</Button>
        </Link>
      </Card>
    );
  }

  if (status === 'error' && error) {
    return (
      <Card title={t('acceptGuardianInvitation.title')}>
        <p role="alert" data-testid="accept-guardian-invitation-error" className={formErrorClassName(error.kind)}>
          {error.message}
        </p>
      </Card>
    );
  }

  return (
    <Card title={t('acceptGuardianInvitation.title')}>
      <p className={secondaryTextClassName} role="status">
        {t('acceptGuardianInvitation.processing')}
      </p>
    </Card>
  );
}
